import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
    MdOutlineAdminPanelSettings,
    MdLogout,
    MdDarkMode,
    MdLightMode,
    MdKeyboardArrowDown,
    MdExitToApp,
} from 'react-icons/md';
import backOfficeRoutes from 'routesBackOffice';
import {
    BACK_OFFICE_SECTIONS,
    isMenuEntryActive,
    useMenuBadges,
} from '../navigation';

const menuRoutes = backOfficeRoutes.filter((route: RoutesType) => route.menu);

const Badge = ({ count }: { count: number }) => (
    <span className="rounded-full bg-[#F0505F] px-1.5 text-[10px] font-semibold leading-4 text-white">
        {count}
    </span>
);

const IconButton = ({
    title,
    onClick,
    className,
    children,
}: {
    title: string;
    onClick: () => void;
    className: string;
    children: React.ReactNode;
}) => (
    <button
        type="button"
        title={title}
        aria-label={title}
        onClick={onClick}
        className={`rounded-md p-1.5 text-[18px] transition-all hover:bg-gray-100 dark:hover:bg-navy-700 ${className}`}
    >
        {children}
    </button>
);

const MenuLink = ({
    route,
    active,
    badge,
}: {
    route: RoutesType;
    active: boolean;
    badge: number;
}) => (
    <Link
        to={route.layout + route.path}
        className={`flex items-center gap-[6px] whitespace-nowrap rounded-[8px] px-[10px] py-[5px] text-sm font-medium text-navy-700 transition-all hover:bg-[#F7FAFC] dark:text-white dark:hover:bg-[#2D3748] ${
            active ? 'bg-[#F7FAFC] dark:bg-[#2D3748]' : ''
        }`}
    >
        <span>{route.icon}</span>
        <span className="flex-1">{route.name}</span>
        {badge > 0 && <Badge count={badge} />}
    </Link>
);

export default function BackOfficeNavbar(props: { currentRoute: string }) {
    const { currentRoute } = props;
    const navigate = useNavigate();
    const [colorMode, setColorMode] = useState<'light' | 'dark'>('light');
    const { pathname } = useLocation();
    const badges = useMenuBadges();
    const badgeOf = (route: RoutesType) =>
        route.badge ? badges[route.badge] || 0 : 0;
    const [openSection, setOpenSection] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Le menu ouvert se ferme en changeant de page ou en cliquant ailleurs.
    useEffect(() => setOpenSection(null), [pathname]);
    useEffect(() => {
        const close = (e: MouseEvent) => {
            if (!menuRef.current?.contains(e.target as Node))
                setOpenSection(null);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem('chakra-ui-color-mode');
        if (stored === 'dark') setColorMode('dark');
    }, []);

    const toggleColorMode = () => {
        const newMode = colorMode === 'light' ? 'dark' : 'light';
        setColorMode(newMode);
        localStorage.setItem('chakra-ui-color-mode', newMode);
        document.documentElement.classList.toggle('dark');
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user-type');
        navigate('/auth/sign-in');
    };

    return (
        <div
            className="fixed left-0 right-0 top-0 z-[999] shadow-md"
            style={{
                background: colorMode === 'dark' ? '#1B254B' : 'white',
            }}
        >
            {/* Une seule ligne : titre, liens directs puis une section par menu déroulant (cf. navigation.tsx), actions */}
            <div className="flex items-center gap-3 px-[20px] py-[6px]">
                <div className="flex shrink-0 items-center gap-1.5 text-sm font-bold text-navy-700 dark:text-white">
                    <MdOutlineAdminPanelSettings className="h-5 w-5" />
                    <span className="hidden xl:inline">Back Office</span>
                </div>
                <span className="h-5 w-px shrink-0 bg-gray-200 dark:bg-navy-600" />
                <div
                    ref={menuRef}
                    className="flex flex-1 flex-wrap items-center gap-[4px]"
                >
                    {menuRoutes
                        .filter((route) => !route.section)
                        .map((route) => (
                            <MenuLink
                                key={route.path}
                                route={route}
                                active={isMenuEntryActive(pathname, route)}
                                badge={badgeOf(route)}
                            />
                        ))}
                    <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-navy-600" />
                    {BACK_OFFICE_SECTIONS.map((section) => {
                        const routes = menuRoutes.filter(
                            (route) => route.section === section.key
                        );
                        if (!routes.length) return null;
                        const active = routes.some((route) =>
                            isMenuEntryActive(pathname, route)
                        );
                        const total = routes.reduce(
                            (sum, route) => sum + badgeOf(route),
                            0
                        );
                        const open = openSection === section.key;
                        return (
                            <div key={section.key} className="relative">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setOpenSection(
                                            open ? null : section.key
                                        )
                                    }
                                    className={`flex items-center gap-1 rounded-[8px] px-[10px] py-[5px] text-sm font-medium text-navy-700 transition-all hover:bg-[#F7FAFC] dark:text-white dark:hover:bg-[#2D3748] ${
                                        active || open
                                            ? 'bg-[#F7FAFC] dark:bg-[#2D3748]'
                                            : ''
                                    }`}
                                >
                                    {active && (
                                        <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
                                    )}
                                    {section.label}
                                    {total > 0 && <Badge count={total} />}
                                    <MdKeyboardArrowDown
                                        className={`h-4 w-4 transition-transform ${
                                            open ? 'rotate-180' : ''
                                        }`}
                                    />
                                </button>
                                {open && (
                                    <div className="absolute left-0 top-full z-[1000] mt-1 flex min-w-[220px] flex-col gap-0.5 rounded-xl border border-gray-100 bg-white p-1.5 shadow-lg dark:border-navy-600 dark:bg-navy-800">
                                        {routes.map((route) => (
                                            <MenuLink
                                                key={route.path}
                                                route={route}
                                                active={isMenuEntryActive(
                                                    pathname,
                                                    route
                                                )}
                                                badge={badgeOf(route)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                    <IconButton
                        title={
                            colorMode === 'dark'
                                ? 'Thème clair'
                                : 'Thème sombre'
                        }
                        onClick={toggleColorMode}
                        className="text-navy-700 dark:text-white"
                    >
                        {colorMode === 'dark' ? (
                            <MdLightMode />
                        ) : (
                            <MdDarkMode />
                        )}
                    </IconButton>
                    <IconButton
                        title="Quitter le back-office"
                        onClick={() => navigate('/admin')}
                        className="text-blue-600 hover:bg-blue-50"
                    >
                        <MdExitToApp />
                    </IconButton>
                    <IconButton
                        title="Se déconnecter"
                        onClick={handleLogout}
                        className="text-red-600 hover:bg-red-50"
                    >
                        <MdLogout />
                    </IconButton>
                </div>
            </div>
        </div>
    );
}
